/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { FiCalendar, FiMapPin, FiSearch } from 'react-icons/fi';
import AppSelect from '../../../../shared/components/app-select/AppSelect';
import AppButton from '../../../../shared/components/app-button/AppButton';
import AppCheckBox from '../../../../shared/components/app-check-box/AppCheckBox';
import { cruiseDestinationsApi } from '../../api/cruiseApi';
import { resultsUrl } from '../../utils/cruiseFlow';
import {
  defaultSearch,
  sailingMonthOptions,
  titleCase,
} from '../../utils/cruiseUtils';
import CruiseGuestsField from './CruiseGuestsField';
import '../../styles/cruise.scss';

/**
 * Cruise search. On the landing page ("Cruises" tab) it starts empty; on
 * the results page it is the modify-search form, filled from the results
 * URL (same as the flight / hotel / bus results pages).
 *
 * Destinations come from CRUISEGATEWAY / DESTINATIONS, loaded when the form
 * mounts; sailing is picked by month, like other cruise portals.
 */
function CruiseSearchForm({ initialSearch, variant, onSearched }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState(() => initialSearch || defaultSearch());
  const [errors, setErrors] = useState({});
  const [destinations, setDestinations] = useState([]);
  const [destLoading, setDestLoading] = useState(true);
  const [destError, setDestError] = useState('');
  const monthOptions = useMemo(() => sailingMonthOptions(), []);

  useEffect(() => {
    const controller = new AbortController();
    cruiseDestinationsApi(controller.signal).then((res) => {
      if (controller.signal.aborted) return;
      setDestinations(
        res.list.map((d) => ({
          value: d.destinationCode,
          label: titleCase(d.label),
          name: d.label,
        })),
      );
      setDestError(res.ok ? '' : res.message || 'Could not load destinations.');
      setDestLoading(false);
    });
    return () => controller.abort();
  }, []);

  const update = (patch) => {
    setSearch((s) => ({ ...s, ...patch }));
    setErrors({});
  };

  const handleSearch = () => {
    const next = {};
    if (!search.destinationCode) next.destination = 'Select a destination';
    if (!search.month) next.month = 'Select a sailing month';
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }
    navigate(resultsUrl(search));
    onSearched();
  };

  const isModify = variant === 'modify';

  let destPlaceholder = 'Select destination';
  if (destLoading) destPlaceholder = 'Loading destinations…';
  else if (destError) destPlaceholder = 'Destinations unavailable';

  return (
    <div
      className={`cruise-module cr-search ${
        isModify ? 'cr-search--modify' : ''
      }`}
    >
      {!isModify && <div className="cr-search__title">Search cruises</div>}
      <div className="cr-search__grid">
        <div
          className={`cr-field cr-field--select ${
            errors.destination ? 'cr-field--error' : ''
          }`}
        >
          <div className="cr-field__label">
            <FiMapPin size={14} />
            Destination
          </div>
          <AppSelect
            name="cruiseDestination"
            label=""
            placeholder={destPlaceholder}
            isClearable={false}
            isLoading={destLoading}
            isDisabled={destLoading}
            options={destinations}
            selectedValue={search.destinationCode}
            appSelectClassName="cr-field-select"
            classNamePrefix="cr-rs"
            height="32px"
            maxheight="320px"
            noOptionsMessage={() => destError || 'No destinations'}
            onSelect={(opt) =>
              update({ destinationCode: opt.value, destinationName: opt.name })
            }
          />
          {errors.destination && (
            <small className="app-error-text">{errors.destination}</small>
          )}
        </div>
        <div
          className={`cr-field cr-field--select ${
            errors.month ? 'cr-field--error' : ''
          }`}
        >
          <div className="cr-field__label">
            <FiCalendar size={14} />
            Sailing month
          </div>
          <AppSelect
            name="cruiseMonth"
            label=""
            placeholder="Select month"
            isClearable={false}
            options={monthOptions}
            selectedValue={search.month}
            appSelectClassName="cr-field-select"
            classNamePrefix="cr-rs"
            isSearchable={false}
            height="32px"
            maxheight="320px"
            onSelect={(opt) => update({ month: opt.value })}
          />
          {errors.month && (
            <small className="app-error-text">{errors.month}</small>
          )}
        </div>
        <CruiseGuestsField
          guests={search}
          onChange={(g) =>
            update({
              adults: g.adults,
              children: g.children,
              infants: g.infants,
              childAges: g.childAges,
              infantAges: g.infantAges,
            })
          }
        />
        <AppButton
          className="cr-search__btn"
          onClick={handleSearch}
          style={{ height: 'auto', borderRadius: '10px', fontWeight: 700 }}
          btnText={
            <>
              <FiSearch size={18} strokeWidth={2.4} />
              {isModify ? 'Modify Search' : 'Search Cruises'}
            </>
          }
        />
      </div>
      <AppCheckBox
        id={isModify ? 'cr-wheelchair-modify' : 'cr-wheelchair'}
        name="wheelchairAccessible"
        checked={Boolean(search.wheelchairAccessible)}
        onChange={() =>
          update({ wheelchairAccessible: !search.wheelchairAccessible })
        }
        labelClassName="cr-check-label cr-search__wheelchair"
        labelText="Wheelchair-accessible stateroom"
      />
    </div>
  );
}

CruiseSearchForm.propTypes = {
  initialSearch: PropTypes.object,
  variant: PropTypes.oneOf(['landing', 'modify']),
  onSearched: PropTypes.func,
};

CruiseSearchForm.defaultProps = {
  initialSearch: null,
  variant: 'landing',
  onSearched: () => {},
};

export default CruiseSearchForm;
